import type { Node } from '../core/models/scene';
import {
  buildLegacyCustomImageUrl,
  isAbsoluteMediaUrl,
  type LegacyCustomImageUrlOptions,
} from './legacyCustomImageUrl';

export const LEGACY_CUSTOM_IMAGE_HASH_RE = /^[a-f0-9]{32}$/i;

export function isLegacyCustomImageHash(value: string): boolean {
  return LEGACY_CUSTOM_IMAGE_HASH_RE.test(value.trim());
}

function readStringOrNestedFile(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object') return '';
  const record = value as { file?: unknown; value?: unknown };
  if (typeof record.file === 'string' && record.file.trim()) return record.file.trim();
  if (typeof record.value === 'string' && record.value.trim()) return record.value.trim();
  return '';
}

export function readLegacyDataImageRawRef(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  const imageSource = data._imageURLSource;
  if (!imageSource || typeof imageSource !== 'object') return '';
  const record = imageSource as { value?: unknown; file?: unknown };
  const fromValue = readStringOrNestedFile(record.value);
  if (fromValue) return fromValue;
  return typeof record.file === 'string' ? record.file.trim() : '';
}

const LEGACY_SHAPE_IMAGE_SLOT_PRIORITY = [
  '_imageURLSource',
  'portrait',
  'profile',
  'avatar',
  'photo',
  'image',
] as const;

function readLegacyImageSlotRawRef(entry: unknown): { rawRef: string; fileType: string } {
  if (!entry || typeof entry !== 'object') {
    return { rawRef: '', fileType: '' };
  }
  const record = entry as { file?: unknown; fileType?: unknown; value?: unknown };
  const rawRef = readStringOrNestedFile(record.file) || readStringOrNestedFile(record.value);
  const fileType = typeof record.fileType === 'string' ? record.fileType.trim().toLowerCase() : '';
  return { rawRef, fileType };
}

export function readLegacyShapeImagesRawRef(
  images: Record<string, unknown> | undefined,
): { rawRef: string; fileType: string } {
  if (!images || typeof images !== 'object') {
    return { rawRef: '', fileType: '' };
  }

  const prioritizedSlots = [
    ...LEGACY_SHAPE_IMAGE_SLOT_PRIORITY,
    ...Object.keys(images).filter(
      (key) => !LEGACY_SHAPE_IMAGE_SLOT_PRIORITY.includes(key as (typeof LEGACY_SHAPE_IMAGE_SLOT_PRIORITY)[number]),
    ),
  ];
  const seen = new Set<string>();

  for (const slot of prioritizedSlots) {
    if (seen.has(slot)) continue;
    seen.add(slot);
    const parsed = readLegacyImageSlotRawRef(images[slot]);
    if (parsed.rawRef) return parsed;
  }

  return { rawRef: '', fileType: '' };
}

export function getNodeAttrString(node: Node, key: string): string {
  const attrs = node.attrs as Record<string, unknown> | undefined;
  const props = (node.props as Record<string, unknown> | undefined) ?? {};
  const propsAttrs = (props.attrs as Record<string, unknown> | undefined) ?? {};
  const raw =
    (typeof attrs?.[key] === 'string' ? attrs[key] : '') ||
    (typeof propsAttrs[key] === 'string' ? propsAttrs[key] : '') ||
    (typeof props[key] === 'string' ? props[key] : '');
  return typeof raw === 'string' ? raw.trim() : '';
}

export function resolveOrgNodePhotoDisplayRef(
  node: Node,
  options?: LegacyCustomImageUrlOptions,
): string {
  const photoRef = getNodeAttrString(node, 'photoRef');
  if (photoRef && isAbsoluteMediaUrl(photoRef)) return photoRef;
  if (photoRef && !photoRef.includes('/') && (isLegacyCustomImageHash(photoRef) || photoRef.length > 0)) {
    const legacyUrl = buildLegacyCustomImageUrl(photoRef, options);
    if (legacyUrl) return legacyUrl;
  }

  const legacyPhotoRef = getNodeAttrString(node, 'legacyPhotoRef');
  if (legacyPhotoRef && isAbsoluteMediaUrl(legacyPhotoRef)) return legacyPhotoRef;
  if (legacyPhotoRef) {
    const legacyUrl = buildLegacyCustomImageUrl(legacyPhotoRef, options);
    if (legacyUrl) return legacyUrl;
  }

  return photoRef;
}

export function resolveOrgNodePhotoIngestUrl(
  node: Node,
  options?: LegacyCustomImageUrlOptions,
): string | null {
  const displayRef = resolveOrgNodePhotoDisplayRef(node, options);
  if (!displayRef) return null;
  if (isAbsoluteMediaUrl(displayRef)) return displayRef;
  return null;
}
